// @ts-nocheck
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Divider, Grid, Typography } from '@mui/material';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import moment from 'moment';
import { CheckCircleOutline, HighlightOff, InfoOutlined } from '@mui/icons-material';
import { useAuth0 } from '@auth0/auth0-react';

import {
  FHAntdDateTimePicker,
  FHButton,
  FHConfirmDialog,
  FHSelect,
  FHTooltip,
} from '../../../../../../base';
import SingleRowInstanceMasteryCriteria from './components/singleRowInstanceMasteryCriteria';
import ErrorBox from '../errorBox';
import SinglePercentageMasteryCriteriaV2 from './components/singleRowPercentageMasteryCriteriaV2';
import { goalActions } from '../../../../../../../store';
import PCGPercentageMasteryCriteriaV1 from './components/pcgPercentageMasteryCriteriaV1';
import SAGPercentageMasteryCriteriaV1 from './components/sagPercentageMasteryCriteriaV1';
import SAGPercentageMasteryCriteriaV2 from './components/sagPercentageMasteryCriteriaV2';
import SAGMasteryCriteriaV1 from './components/sagMasteryCriteriaV1';
import {
  MigrationModalPCGContent,
  MigrationModalSAGV1GoalContent,
  MigrationModalSAGV2GoalContent,
} from './components/migrationModalContent';
import NumericInput from '../numericInput';
import { capitalizeFirstLetter, disabledFutureDate } from '../../../../../../../helpers';
import { getDataCollectionMethod } from './utils/general';
import { BackgroundTypes, Themes } from '../../../../../../../constants';
import { FORTA_GRAY2, FORTA_ORANGE, FORTA_WHITE } from '../../../../../../../utils/colors';
import { getRangeArray } from '../../../../../../../utils/common';
import {
  BaselineMeasurementVersion,
  DefaultDurationMCValues,
  DefaultDurationMeasurementCriteria,
  DefaultInstanceMCValues,
  DefaultMeasurementUnitValueV2Goal,
  DefaultPCGMeasurementUnitValueV1Goal,
  DefaultPercentageDCMValueV1Goal,
  DefaultPercentageDCMValueV2Goal,
  DefaultPercentageDCMValueV2GoalV1Subresource,
  DefaultRatesMCValues,
  DefaultRatesMeasurementCriteria,
  DefaultSAGMeasurementUnitValueV1Goal,
  DurationEventMeasurementUnitEnum,
  GoalTypes,
  RateMethodMeasurementUnitsIntervalUnitEnum,
  TPGoalDataCollectionMethod,
  TreatmentPlanResourceVersion,
} from '../../constants/general';
import SingleRowRatesMasteryCriteria from './components/singleRowRatesMasteryCriteria';
import SAGRateMasteryCriteriaV2 from './components/sagRateMasteryCriteriaV2';
import SingleRowDurationMasteryCriteria from './components/singleRowDurationMasteryCriteria';
import SAGDurationMasteryCriteriaV2 from './components/sagDurationMasteryCriteriaV2';
import SAGInstanceMasteryCriteriaV2 from './components/sagInstanceMasteryCriteriaV2';

import type {
  ITreatmentPlanGoal,
  SelectValueType,
} from '../../../../../../../utils/types';
import type { RootState } from '../../../../../../../store';
import type { Moment } from 'moment';
import type { IMigrationPreviewState } from './types/states';

interface BaselineAndMasteryCriteriaProps {
  goal: ITreatmentPlanGoal;
  isEditable: boolean;
  treatmentPlanId: number;
  goalIndex: number;
  errors: any;
  goalVersion: TreatmentPlanResourceVersion;
  onUpdateField: (key: string, value: any) => void;
}

const getTotalAttempts = (measurementUnits: ITreatmentPlanGoal['measurement_units']) =>
  measurementUnits && 'total_attempts' in measurementUnits ? measurementUnits.total_attempts : null;

const getIntervalUnits = (measurementUnits: ITreatmentPlanGoal['measurement_units']) =>
  measurementUnits && 'interval_size' in measurementUnits ? measurementUnits : null;

const getDurationUnits = (measurementUnits: ITreatmentPlanGoal['measurement_units']) =>
  measurementUnits && 'duration_unit' in measurementUnits ? measurementUnits : null;

const getPercentageCriteria = (masteryCriteria: ITreatmentPlanGoal['mastery_criteria']) =>
  masteryCriteria && 'desired_amount' in masteryCriteria ? masteryCriteria : null;

type GoalMasteryCriteriaValue =
  | NonNullable<ReturnType<typeof DefaultDurationMCValues>>
  | NonNullable<ReturnType<typeof DefaultRatesMCValues>>
  | NonNullable<ReturnType<typeof DefaultInstanceMCValues>>
  | NonNullable<ReturnType<typeof DefaultPercentageDCMValueV1Goal>>
  | NonNullable<ReturnType<typeof DefaultPercentageDCMValueV2Goal>>
  | NonNullable<ReturnType<typeof DefaultPercentageDCMValueV2GoalV1Subresource>>;

function BaselineAndMasteryCriteria({
  goal,
  isEditable,
  errors,
  treatmentPlanId,
  goalIndex,
  goalVersion,
  onUpdateField,
}: BaselineAndMasteryCriteriaProps) {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();

  const { originalGoals } = useSelector((state: RootState) => state.goals);

  const [showConfirmation, setShowConfirmation] = useState(false);
  const [migrationPreviewState, setMigrationPreviewState] = useState<IMigrationPreviewState | null>(
    null,
  );

  const [baselineV2TotalAttemts, setBaselineV2TotalAttempts] = useState<number | null>(
    goal?.resource_versions?.baseline_measurement === BaselineMeasurementVersion.V2
      ? getTotalAttempts(goal.measurement_units)
      : getPercentageCriteria(goal.mastery_criteria)?.total_attempts ?? null,
  );

  useEffect(() => {
    setBaselineV2TotalAttempts(
      getTotalAttempts(goal.measurement_units),
    );
  }, [goal?.resource_versions?.baseline_measurement]);

  const dataCollectionMethodOptions = useMemo(() => {
    return (
      getDataCollectionMethod(goal.goal_type).map((method) => ({
        value: method,
        label: capitalizeFirstLetter(method.toLowerCase()),
      })) ?? []
    );
  }, [goal.goal_type]);

  const handleDataCollectionMethodUpdate = (value: SelectValueType) => {
    if (goalVersion === TreatmentPlanResourceVersion.V1) {
      handleV1DataCollectionMethodChange(value);
    } else {
      handleV2DataCollectionMethodChange(value);
    }
  };

  const handleV1DataCollectionMethodChange = (value: SelectValueType) => {
    // Choosing the following initial structure for MC as v1 and v2 version is only
    // applicable for PERCENTAGE SAG and PCG goals
    let mastery_criteria: GoalMasteryCriteriaValue = {
      desired_amount: null,
      practice_duration_count: null,
      total_attempts: null,
    };
    let measurement_units: object | null = null;

    switch (value) {
      case TPGoalDataCollectionMethod.DURATION:
        mastery_criteria = DefaultDurationMCValues(
          goal?.goal_type,
          TreatmentPlanResourceVersion.V1,
        );
        measurement_units = { ...DefaultDurationMeasurementCriteria };
        break;
      case TPGoalDataCollectionMethod.RATE:
        mastery_criteria = DefaultRatesMCValues(goal?.goal_type, TreatmentPlanResourceVersion.V1);
        measurement_units = { ...DefaultRatesMeasurementCriteria };
        break;
      case TPGoalDataCollectionMethod.INSTANCES:
        mastery_criteria = DefaultInstanceMCValues(
          goal?.goal_type,
          TreatmentPlanResourceVersion.V1,
        );
        measurement_units = null;
        break;
      case TPGoalDataCollectionMethod.PERCENTAGE:
        if (
          goal?.goal_type === GoalTypes.SkillAcquisition &&
          goal?.resource_versions?.baseline_measurement === BaselineMeasurementVersion.V2
        ) {
          mastery_criteria = {
            ...DefaultPercentageDCMValueV1Goal(GoalTypes.SkillAcquisition),
          };
          measurement_units = {
            ...DefaultSAGMeasurementUnitValueV1Goal,
          };
        }
        if (
          goal?.goal_type === GoalTypes.Caregiver &&
          goal?.resource_versions?.baseline_measurement === BaselineMeasurementVersion.V2
        ) {
          mastery_criteria = {
            ...DefaultPercentageDCMValueV1Goal(GoalTypes.Caregiver),
          };
          measurement_units = {
            ...DefaultPCGMeasurementUnitValueV1Goal,
          };
        }
        break;
    }
    dispatch(
      goalActions.updateGoalProperty({
        goalIndex,
        goalType: goal?.goal_type,
        key: 'mastery_criteria',
        value: mastery_criteria,
      }),
    );
    dispatch(
      goalActions.updateGoalProperty({
        goalIndex,
        goalType: goal?.goal_type,
        key: 'measurement_units',
        value: measurement_units,
      }),
    );
    dispatch(
      goalActions.updateGoalProperty({
        goalIndex,
        goalType: goal?.goal_type,
        key: 'data_collection_method',
        value: value,
      }),
    );
    dispatch(
      goalActions.updateGoalProperty({
        goalIndex,
        goalType: goal.goal_type,
        key: 'dirty',
        value: true,
      }),
    );
  };

  const handleV2DataCollectionMethodChange = (value: SelectValueType) => {
    let mastery_criteria = {};
    let measurement_units: object | null = null;
    switch (value) {
      case TPGoalDataCollectionMethod.DURATION:
        mastery_criteria = DefaultDurationMCValues(
          goal?.goal_type,
          TreatmentPlanResourceVersion.V2,
        );
        measurement_units = { ...DefaultDurationMeasurementCriteria };
        break;
      case TPGoalDataCollectionMethod.RATE:
        mastery_criteria = DefaultRatesMCValues(goal?.goal_type, TreatmentPlanResourceVersion.V2);
        measurement_units = { ...DefaultRatesMeasurementCriteria };
        break;
      case TPGoalDataCollectionMethod.INSTANCES:
        mastery_criteria = DefaultInstanceMCValues(
          goal?.goal_type,
          TreatmentPlanResourceVersion.V2,
        );
        measurement_units = null;
        break;
      case TPGoalDataCollectionMethod.PERCENTAGE:
        if (
          goal?.goal_type === GoalTypes.SkillAcquisition &&
          goal?.resource_versions?.baseline_measurement === BaselineMeasurementVersion.V2
        ) {
          mastery_criteria = {
            ...DefaultPercentageDCMValueV2Goal(GoalTypes.SkillAcquisition),
          };
          measurement_units = DefaultMeasurementUnitValueV2Goal;
        }
        if (
          goal?.goal_type === GoalTypes.SkillAcquisition &&
          goal?.resource_versions?.baseline_measurement === BaselineMeasurementVersion.V1
        ) {
          mastery_criteria = {
            ...DefaultPercentageDCMValueV2GoalV1Subresource(GoalTypes.SkillAcquisition),
          };
          measurement_units = null;
        }
        if (
          goal?.goal_type === GoalTypes.Caregiver &&
          goal?.resource_versions?.baseline_measurement === BaselineMeasurementVersion.V2
        ) {
          mastery_criteria = {
            ...DefaultPercentageDCMValueV2Goal(GoalTypes.Caregiver),
          };
          measurement_units = DefaultMeasurementUnitValueV2Goal;
        }
        if (
          goal?.goal_type === GoalTypes.Caregiver &&
          goal?.resource_versions?.baseline_measurement === BaselineMeasurementVersion.V1
        ) {
          mastery_criteria = {
            ...DefaultPercentageDCMValueV2GoalV1Subresource(GoalTypes.Caregiver),
          };
          measurement_units = null;
        }
        break;
    }
    dispatch(
      goalActions.updateGoalProperty({
        goalIndex,
        goalType: goal.goal_type,
        key: 'mastery_criteria',
        value: mastery_criteria,
      }),
    );
    dispatch(
      goalActions.updateGoalProperty({
        goalIndex,
        goalType: goal.goal_type,
        key: 'measurement_units',
        value: measurement_units,
      }),
    );
    dispatch(
      goalActions.updateGoalProperty({
        goalIndex,
        goalType: goal?.goal_type,
        key: 'data_collection_method',
        value: value,
      }),
    );
    dispatch(
      goalActions.updateGoalProperty({
        goalIndex,
        goalType: goal.goal_type,
        key: 'dirty',
        value: true,
      }),
    );
  };

  const handleBaselineVersionUpdate = async (preview = true) => {
    const response = await dispatch(
      goalActions.migrateTreatmentPlanResourceVersion({
        getAccessTokenSilently,
        treatmentPlanId,
        goalId: goal?.id,
        preview,
        goalType: goal?.goal_type,
        data: {
          baseline_measurement: BaselineMeasurementVersion.V2,
        },
      }),
    );
    if (!response.error) {
      const { mastery_criteria, measurement_units, baseline_at_creation, resource_versions } =
        response.payload;
      setShowConfirmation(true);
      setMigrationPreviewState({
        baseline_at_creation,
        mastery_criteria,
        measurement_units,
        resource_versions,
      });
      if (!preview) {
        const persistedOriginalGoal = originalGoals?.[goal.goal_type]?.[goalIndex];
        if (persistedOriginalGoal) {
          dispatch(
            goalActions.updateOriginalGoal({
              goalIndex,
              goalType: goal?.goal_type,
              goal: {
                ...persistedOriginalGoal,
                mastery_criteria: { ...mastery_criteria },
                measurement_units,
                baseline_at_creation,
                resource_versions: { ...resource_versions },
              },
            }),
          );
        }
        dispatch(
          goalActions.updateGoalProperty({
            goalIndex,
            goalType: goal?.goal_type,
            key: 'mastery_criteria',
            value: mastery_criteria,
          }),
        );
        dispatch(
          goalActions.updateGoalProperty({
            goalIndex,
            goalType: goal?.goal_type,
            key: 'measurement_units',
            value: measurement_units,
          }),
        );
        dispatch(
          goalActions.updateGoalProperty({
            goalIndex,
            goalType: goal?.goal_type,
            key: 'baseline_at_creation',
            value: baseline_at_creation,
          }),
        );
        dispatch(
          goalActions.updateGoalProperty({
            goalIndex,
            goalType: goal?.goal_type,
            key: 'resource_versions',
            value: resource_versions,
          }),
        );
        handleCloseMigrationModal();
      }
    }
  };

  const handleCloseMigrationModal = () => {
    setShowConfirmation(false);
    setMigrationPreviewState(null);
  };

  return (
    <Box mt={1}>
      {goal?.resource_versions?.baseline_measurement === BaselineMeasurementVersion.V1 &&
        goal?.is_editing && <Divider sx={{ color: FORTA_GRAY2, borderWidth: '1px', my: 3 }} />}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={3.5}>
          <Typography fontWeight={'bold'} align="left" mb={1}>
            Data collection method*
          </Typography>
          {goal?.is_editing ? (
            <FHSelect
              data-testid="dataCollectionMethodDropDown"
              sx={{ minWidth: 150 }}
              inputHeight={40}
              colorTheme={Themes.Primary}
              backgroundType={BackgroundTypes.Colored}
              fullWidth
              displayEmpty
              disabled={!isEditable}
              renderValue={
                goal.data_collection_method === null
                  ? () => <Typography>Select</Typography>
                  : undefined
              }
              options={dataCollectionMethodOptions}
              optionDataTestIdPrefix="dataCollectionMethodOption"
              value={goal.data_collection_method ? goal.data_collection_method : null}
              onUpdate={(value: SelectValueType) => {
                handleDataCollectionMethodUpdate(value);
              }}
              error={errors && errors['data_collection_method']}
            />
          ) : (
            <Typography align="left">
              {goal.data_collection_method
                ? capitalizeFirstLetter(goal.data_collection_method.toLowerCase())
                : ''}
            </Typography>
          )}
        </Grid>
        {goal.data_collection_method === TPGoalDataCollectionMethod.RATE && (
          <>
            <Grid item xs={3}>
              <Typography fontWeight={'bold'} align="left" mb={1}>
                Interval*
              </Typography>
              {goal?.is_editing ? (
                <FHSelect
                  data-testid="rateDCMIntervalDropDown"
                  sx={{ minWidth: 150 }}
                  inputHeight={40}
                  colorTheme={Themes.Primary}
                  backgroundType={BackgroundTypes.Colored}
                  fullWidth
                  displayEmpty
                  disabled={!isEditable}
                  renderValue={
                    getIntervalUnits(goal.measurement_units)?.interval_size === null
                      ? () => <Typography>Select</Typography>
                      : undefined
                  }
                  options={
                    getIntervalUnits(goal.measurement_units)?.interval_unit ===
                    RateMethodMeasurementUnitsIntervalUnitEnum.HOUR
                      ? getRangeArray(1, 4).map((num) => ({
                          value: num,
                          label: num,
                        }))
                      : getRangeArray(1, 60).map((num) => ({
                          value: num,
                          label: num,
                        }))
                  }
                  optionDataTestIdPrefix="rateDCMIntervalDropDownOption"
                  value={getIntervalUnits(goal.measurement_units)?.interval_size ?? null}
                  onUpdate={(value: SelectValueType) => {
                    dispatch(
                      goalActions.updateGoalProperty({
                        goalIndex,
                        goalType: goal.goal_type,
                        key: 'measurement_units',
                        value: {
                          ...(goal?.measurement_units ?? {}),
                          interval_size: value,
                        },
                      }),
                    );
                    dispatch(
                      goalActions.updateGoalProperty({
                        goalIndex,
                        goalType: goal.goal_type,
                        key: 'dirty',
                        value: true,
                      }),
                    );
                  }}
                />
              ) : (
                <Typography align="left">
                  {getIntervalUnits(goal.measurement_units)?.interval_size
                    ? getIntervalUnits(goal.measurement_units)?.interval_size
                    : ''}
                </Typography>
              )}
            </Grid>

            <Grid item xs={3}>
              <Typography fontWeight={'bold'} align="left" mb={1}>
                Interval Unit*
              </Typography>
              {goal?.is_editing ? (
                <FHSelect
                  data-testid="rateDCMIntervalUnitDropDown"
                  sx={{ minWidth: 150 }}
                  inputHeight={40}
                  colorTheme={Themes.Primary}
                  backgroundType={BackgroundTypes.Colored}
                  fullWidth
                  displayEmpty
                  disabled={!isEditable}
                  renderValue={
                    goal.data_collection_method === null
                      ? () => <Typography>Select</Typography>
                      : undefined
                  }
                  options={[
                    { value: RateMethodMeasurementUnitsIntervalUnitEnum.SECOND, label: 'Seconds' },
                    { value: RateMethodMeasurementUnitsIntervalUnitEnum.MINUTE, label: 'Minutes' },
                    { value: RateMethodMeasurementUnitsIntervalUnitEnum.HOUR, label: 'Hours' },
                  ]}
                  optionDataTestIdPrefix="rateDCMIntervalUnitDropDownOptions"
                  value={getIntervalUnits(goal.measurement_units)?.interval_unit ?? null}
                  onUpdate={(value: SelectValueType) => {
                    const intervalSizeValue =
                      value === RateMethodMeasurementUnitsIntervalUnitEnum.HOUR &&
                      getIntervalUnits(goal.measurement_units)?.interval_size > 4
                        ? 1
                        : getIntervalUnits(goal.measurement_units)?.interval_size;
                    dispatch(
                      goalActions.updateGoalProperty({
                        goalIndex,
                        goalType: goal.goal_type,
                        key: 'measurement_units',
                        value: {
                          ...(goal?.measurement_units ?? {}),
                          interval_unit: value,
                          interval_size: intervalSizeValue,
                        },
                      }),
                    );
                    dispatch(
                      goalActions.updateGoalProperty({
                        goalIndex,
                        goalType: goal.goal_type,
                        key: 'dirty',
                        value: true,
                      }),
                    );
                  }}
                />
              ) : (
                <Typography align="left">
                  {getIntervalUnits(goal.measurement_units)?.interval_unit
                    ? getIntervalUnits(goal.measurement_units)?.interval_unit
                    : ''}
                </Typography>
              )}
            </Grid>
          </>
        )}

        {goal.data_collection_method === TPGoalDataCollectionMethod.DURATION && (
          <>
            <Grid item xs={3}>
              <Typography fontWeight={'bold'} align="left" mb={1}>
                Duration units*
              </Typography>
              {goal?.is_editing ? (
                <FHSelect
                  data-testid="durationDCMunitDropdown"
                  sx={{ minWidth: 150 }}
                  inputHeight={40}
                  colorTheme={Themes.Primary}
                  backgroundType={BackgroundTypes.Colored}
                  fullWidth
                  displayEmpty
                  disabled={!isEditable}
                  renderValue={
                    getIntervalUnits(goal.measurement_units)?.interval_size === null
                      ? () => <Typography>Select</Typography>
                      : undefined
                  }
                  options={[
                    {
                      value: DurationEventMeasurementUnitEnum.hour,
                      label: capitalizeFirstLetter(
                        DurationEventMeasurementUnitEnum.hour.toLowerCase(),
                      ),
                    },
                    {
                      value: DurationEventMeasurementUnitEnum.minute,
                      label: capitalizeFirstLetter(
                        DurationEventMeasurementUnitEnum.minute.toLowerCase(),
                      ),
                    },
                    {
                      value: DurationEventMeasurementUnitEnum.second,
                      label: capitalizeFirstLetter(
                        DurationEventMeasurementUnitEnum.second.toLowerCase(),
                      ),
                    },
                  ]}
                  optionDataTestIdPrefix="durationDCMunitDropdownOption"
                  value={
                    getDurationUnits(goal.measurement_units)?.duration_unit ?? null
                  }
                  onUpdate={(value: SelectValueType) => {
                    dispatch(
                      goalActions.updateGoalProperty({
                        goalIndex,
                        goalType: goal.goal_type,
                        key: 'measurement_units',
                        value: {
                          duration_unit: value,
                        },
                      }),
                    );
                    dispatch(
                      goalActions.updateGoalProperty({
                        goalIndex,
                        goalType: goal.goal_type,
                        key: 'dirty',
                        value: true,
                      }),
                    );
                  }}
                />
              ) : (
                <Typography align="left">
                  {capitalizeFirstLetter(
                    getDurationUnits(goal.measurement_units)?.duration_unit?.toLowerCase(),
                  )}
                </Typography>
              )}
            </Grid>
          </>
        )}

        {goal?.resource_versions?.baseline_measurement === BaselineMeasurementVersion.V1 &&
          goal?.goal_type !== GoalTypes.BehaviorReduction &&
          goal?.data_collection_method === TPGoalDataCollectionMethod.PERCENTAGE &&
          goal?.is_editing && (
            <Grid item xs={8.5} alignItems={'flex-start'}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end' }}>
                <FHButton sx={{ mt: 4 }} onClick={() => handleBaselineVersionUpdate()}>
                  Update format
                </FHButton>
              </Box>
            </Grid>
          )}
      </Grid>
      {goal.data_collection_method && (
        <Grid container spacing={3} mb={3}>
          <Grid item xs={3.5}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography fontWeight={'bold'} align="left" mb={1}>
                {goalVersion === TreatmentPlanResourceVersion.V2
                  ? 'Baseline date*'
                  : 'Date of introduction*'}
              </Typography>
              {(goal?.hypothesized_functions?.length ?? 0) > 1 && goal?.is_editing && (
                <FHTooltip
                  arrow
                  title={
                    <Typography>
                      Field will be disabled when the hypothesized function table contains 2 or more
                      rows.
                    </Typography>
                  }
                  placement="top"
                  sx={{
                    borderRadius: '10px',
                    display: 'flex',
                    maxWidth: '350px',
                  }}
                  tooltipbackground={FORTA_WHITE}
                  tooltipBoxShadow={'0px 4px 4px rgba(0, 0, 0, 0.25)'}
                >
                  <InfoOutlined sx={{ color: FORTA_ORANGE, ml: 1, mb: 1 }} />
                </FHTooltip>
              )}
            </Box>
            {goal?.is_editing ? (
              <FHAntdDateTimePicker
                data-testid="goalIntroductionDatePicker"
                format="MM/DD/YY"
                allowClear={false}
                colorTheme={Themes.Primary}
                coloredBackground
                inputHeight={40}
                style={{ width: '100%' }}
                disabled={!isEditable || (goal?.hypothesized_functions?.length ?? 0) > 1}
                disabledDate={disabledFutureDate}
                value={goal.date_introduced ? moment(goal.date_introduced) : ''}
                onChange={(value: Moment) =>
                  onUpdateField('date_introduced', value.format('YYYY-MM-DD'))
                }
                onSelect={(value: Moment) =>
                  onUpdateField('date_introduced', value.format('YYYY-MM-DD'))
                }
                error={errors && errors['date_introduced']}
              />
            ) : (
              <Typography align="left">
                {goal.date_introduced ? moment(goal.date_introduced).format('MM/DD/YY') : ''}
              </Typography>
            )}
          </Grid>
          <Grid item xs={3}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography fontWeight={'bold'} align="left" mb={1}>
                Baseline level*
              </Typography>
              {(goal?.hypothesized_functions?.length ?? 0) > 1 && goal?.is_editing && (
                <FHTooltip
                  arrow
                  title={
                    <Typography>
                      Field will be disabled when the hypothesized function table contains 2 or more
                      rows.
                    </Typography>
                  }
                  placement="top"
                  sx={{
                    borderRadius: '10px',
                    display: 'flex',
                    maxWidth: '350px',
                  }}
                  tooltipbackground={FORTA_WHITE}
                  tooltipBoxShadow={'0px 4px 4px rgba(0, 0, 0, 0.25)'}
                >
                  <InfoOutlined sx={{ color: FORTA_ORANGE, ml: 1, mb: 1 }} />
                </FHTooltip>
              )}
            </Box>
            {goal?.is_editing ? (
              <FHSelect
                data-testid="baseLineLevelDropDown"
                sx={{ minWidth: 150 }}
                inputHeight={40}
                colorTheme={Themes.Primary}
                backgroundType={BackgroundTypes.Colored}
                fullWidth
                displayEmpty
                disabled={!isEditable || (goal?.hypothesized_functions?.length ?? 0) > 1}
                renderValue={
                  goal.baseline_at_creation === null
                    ? () => <Typography>Select baseline</Typography>
                    : undefined
                }
                options={getRangeArray(0, 100).map((num) => ({
                  value: num,
                  label: num,
                }))}
                optionDataTestIdPrefix="baseLineLevelOption"
                value={
                  goal.baseline_at_creation || goal.baseline_at_creation === 0
                    ? goal.baseline_at_creation
                    : null
                }
                onUpdate={(value: SelectValueType) => onUpdateField('baseline_at_creation', value)}
                error={
                  (errors && errors['baseline_at_creation']) ||
                  (getTotalAttempts(goal.measurement_units) &&
                    goal?.resource_versions?.baseline_measurement ===
                      BaselineMeasurementVersion.V2 &&
                    Number(goal.baseline_at_creation) >= getTotalAttempts(goal.measurement_units))
                }
                helperTextAlign="left"
                helperText={
                  getTotalAttempts(goal.measurement_units) &&
                  goal?.resource_versions?.baseline_measurement === BaselineMeasurementVersion.V2 &&
                  Number(goal.baseline_at_creation) >= getTotalAttempts(goal.measurement_units)
                    ? 'Baseline level should be less than total attempts'
                    : ''
                }
              />
            ) : goal.baseline_at_creation === '' || goal.baseline_at_creation === null ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  border: '2px solid red',
                  width: 200,
                  height: 40,
                  borderRadius: 1.5,
                }}
              ></Box>
            ) : goal.baseline_at_creation === null ? (
              <ErrorBox />
            ) : (
              <>
                {goal.data_collection_method === TPGoalDataCollectionMethod.INSTANCES && (
                  <Typography align="left">{`${goal.baseline_at_creation} instances`}</Typography>
                )}
                {goal.data_collection_method === TPGoalDataCollectionMethod.DURATION && (
                  <Typography align="left">{`${goal.baseline_at_creation} ${getDurationUnits(
                    goal.measurement_units,
                  )?.duration_unit?.toLowerCase()}`}</Typography>
                )}
                {goal.data_collection_method === TPGoalDataCollectionMethod.PERCENTAGE &&
                  goal?.resource_versions?.baseline_measurement ===
                    BaselineMeasurementVersion.V1 && (
                    <Typography align="left">{`${goal?.baseline_at_creation}`}</Typography>
                  )}
                {goal.data_collection_method === TPGoalDataCollectionMethod.RATE && (
                  <Typography align="left">{`${goal?.baseline_at_creation}`}</Typography>
                )}
                {goal.data_collection_method === TPGoalDataCollectionMethod.PERCENTAGE &&
                  goal?.resource_versions?.baseline_measurement ===
                    BaselineMeasurementVersion.V2 && (
                    <Typography align="left">{`${goal?.baseline_at_creation} out of ${
                      getTotalAttempts(goal.measurement_units)
                    }  opportunities`}</Typography>
                  )}
              </>
            )}
          </Grid>
          {goal?.is_editing &&
            goal.data_collection_method === TPGoalDataCollectionMethod.INSTANCES && (
              <Grid item xs={3} mt={5} ml={-1}>
                <Typography align="left">instances</Typography>
              </Grid>
            )}

          {goal?.is_editing &&
            goal.data_collection_method === TPGoalDataCollectionMethod.DURATION && (
              <Grid item xs={3} mt={5} ml={-1}>
                <Typography align="left">{`${getDurationUnits(
                  goal.measurement_units,
                )?.duration_unit?.toLowerCase()}`}</Typography>
              </Grid>
            )}

          {goal?.is_editing &&
            goal.data_collection_method === TPGoalDataCollectionMethod.PERCENTAGE &&
            goal?.resource_versions?.baseline_measurement === BaselineMeasurementVersion.V2 && (
              <Grid item xs={5} mt={4} ml={-1}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography align="left">out of</Typography>
                  <NumericInput
                    data-testid={`totalAtempts-${goalIndex}`}
                    sx={{
                      width: 45,
                      minWidth: 45,
                      borderRadius: 1.5,
                      mx: 0.5,
                    }}
                    disabled={!isEditable}
                    value={baselineV2TotalAttemts ?? null}
                    onChange={(value) => {
                      const newVal = value ? parseInt(value) : null;
                      setBaselineV2TotalAttempts(newVal);
                      dispatch(
                        goalActions.updateGoalProperty({
                          goalIndex,
                          goalType: goal.goal_type,
                          key:
                            goal?.resource_versions?.baseline_measurement ===
                            BaselineMeasurementVersion.V2
                              ? 'measurement_units'
                              : 'mastery_criteria',
                          value:
                            goal?.resource_versions?.baseline_measurement ===
                            BaselineMeasurementVersion.V2
                              ? {
                                  total_attempts: newVal,
                                }
                              : {
                                  ...(goal?.mastery_criteria ?? {}),
                                  total_attempts: newVal,
                                },
                        }),
                      );
                      dispatch(
                        goalActions.updateGoalProperty({
                          goalIndex,
                          goalType: goal.goal_type,
                          key: 'dirty',
                          value: true,
                        }),
                      );
                    }}
                    error={
                      goal?.resource_versions?.baseline_measurement ===
                      BaselineMeasurementVersion.V2
                        ? !getTotalAttempts(goal.measurement_units)
                        : !getPercentageCriteria(goal.mastery_criteria)?.total_attempts
                    }
                  />
                  <Typography align="left" ml={1}>
                    opportunities
                  </Typography>
                </Box>
              </Grid>
            )}
        </Grid>
      )}
      {goal.data_collection_method && (
        <Box mt={1}>
          <Typography fontWeight={700} align="left" mb={1}>
            Mastering criteria*
          </Typography>
          {/* Rates */}
          {goal?.data_collection_method === TPGoalDataCollectionMethod.RATE &&
            goalVersion === TreatmentPlanResourceVersion.V1 && (
              <SingleRowRatesMasteryCriteria
                goal={goal}
                isEditable={isEditable}
                goalIndex={goalIndex}
              />
            )}
          {goal?.data_collection_method === TPGoalDataCollectionMethod.RATE &&
            goalVersion === TreatmentPlanResourceVersion.V2 &&
            goal?.goal_type !== GoalTypes.SkillAcquisition && (
              <SingleRowRatesMasteryCriteria
                goal={goal}
                isEditable={isEditable}
                goalIndex={goalIndex}
              />
            )}
          {goal?.data_collection_method === TPGoalDataCollectionMethod.RATE &&
            goalVersion === TreatmentPlanResourceVersion.V2 &&
            goal?.goal_type === GoalTypes.SkillAcquisition && (
              <SAGRateMasteryCriteriaV2 goal={goal} isEditable={isEditable} goalIndex={goalIndex} />
            )}

          {/* Duration */}
          {goal?.data_collection_method === TPGoalDataCollectionMethod.DURATION &&
            goalVersion === TreatmentPlanResourceVersion.V1 && (
              <SingleRowDurationMasteryCriteria
                goal={goal}
                isEditable={isEditable}
                goalIndex={goalIndex}
                isPCGGoal={goal?.goal_type === GoalTypes.Caregiver}
              />
            )}
          {goal?.data_collection_method === TPGoalDataCollectionMethod.DURATION &&
            goalVersion === TreatmentPlanResourceVersion.V2 &&
            goal?.goal_type !== GoalTypes.SkillAcquisition && (
              <SingleRowDurationMasteryCriteria
                goal={goal}
                isEditable={isEditable}
                goalIndex={goalIndex}
                isPCGGoal={goal?.goal_type === GoalTypes.Caregiver}
              />
            )}
          {goal?.data_collection_method === TPGoalDataCollectionMethod.DURATION &&
            goalVersion === TreatmentPlanResourceVersion.V2 &&
            goal?.goal_type === GoalTypes.SkillAcquisition && (
              <SAGDurationMasteryCriteriaV2
                goal={goal}
                isEditable={isEditable}
                goalIndex={goalIndex}
              />
            )}

          {/* Instances */}
          {goal?.data_collection_method === TPGoalDataCollectionMethod.INSTANCES &&
            goalVersion === TreatmentPlanResourceVersion.V1 && (
              <SingleRowInstanceMasteryCriteria
                goal={goal}
                isEditable={isEditable}
                goalIndex={goalIndex}
                goalVersion={goalVersion}
                isBRG={goal?.goal_type === GoalTypes.BehaviorReduction}
              />
            )}

          {goal?.data_collection_method === TPGoalDataCollectionMethod.INSTANCES &&
            goal?.goal_type !== GoalTypes.SkillAcquisition &&
            goalVersion === TreatmentPlanResourceVersion.V2 && (
              <SingleRowInstanceMasteryCriteria
                goal={goal}
                isEditable={isEditable}
                goalIndex={goalIndex}
                goalVersion={goalVersion}
                isBRG={goal?.goal_type === GoalTypes.BehaviorReduction}
              />
            )}

          {goal?.data_collection_method === TPGoalDataCollectionMethod.INSTANCES &&
            goal?.goal_type === GoalTypes.SkillAcquisition &&
            goalVersion === TreatmentPlanResourceVersion.V2 && (
              <SAGInstanceMasteryCriteriaV2
                goal={goal}
                isEditable={isEditable}
                goalIndex={goalIndex}
                goalVersion={goalVersion}
              />
            )}

          {/* Percentage - SAG - V1 */}
          {goal?.data_collection_method === TPGoalDataCollectionMethod.PERCENTAGE &&
            goal?.goal_type === GoalTypes.SkillAcquisition &&
            goalVersion === TreatmentPlanResourceVersion.V1 &&
            goal?.resource_versions?.baseline_measurement === BaselineMeasurementVersion.V1 && (
              <SAGMasteryCriteriaV1
                goal={goal}
                isEditable={isEditable}
                goalIndex={goalIndex}
                goalVersion={goalVersion}
              />
            )}
          {goal?.data_collection_method === TPGoalDataCollectionMethod.PERCENTAGE &&
            goal?.goal_type === GoalTypes.SkillAcquisition &&
            goalVersion === TreatmentPlanResourceVersion.V1 &&
            goal?.resource_versions?.baseline_measurement === BaselineMeasurementVersion.V2 && (
              <SinglePercentageMasteryCriteriaV2
                goal={goal}
                isEditable={isEditable}
                goalIndex={goalIndex}
                goalVersion={goalVersion}
              />
            )}

          {/* Percentage - SAG - V2 */}
          {goal?.data_collection_method === TPGoalDataCollectionMethod.PERCENTAGE &&
            goal?.goal_type === GoalTypes.SkillAcquisition &&
            goalVersion === TreatmentPlanResourceVersion.V2 &&
            goal?.resource_versions?.baseline_measurement === BaselineMeasurementVersion.V1 && (
              <SAGPercentageMasteryCriteriaV1
                goal={goal}
                isEditable={isEditable}
                goalIndex={goalIndex}
              />
            )}
          {goal?.data_collection_method === TPGoalDataCollectionMethod.PERCENTAGE &&
            goal?.goal_type === GoalTypes.SkillAcquisition &&
            goalVersion === TreatmentPlanResourceVersion.V2 &&
            goal?.resource_versions?.baseline_measurement === BaselineMeasurementVersion.V2 && (
              <SAGPercentageMasteryCriteriaV2
                goal={goal}
                isEditable={isEditable}
                goalIndex={goalIndex}
              />
            )}

          {/* Percentage - PCG - V1 mastery criteria - same for v1 and v2 goal */}
          {goal?.data_collection_method === TPGoalDataCollectionMethod.PERCENTAGE &&
            goal?.goal_type === GoalTypes.Caregiver &&
            goal?.resource_versions?.baseline_measurement === BaselineMeasurementVersion.V1 && (
              <PCGPercentageMasteryCriteriaV1
                goal={goal}
                isEditable={isEditable}
                goalIndex={goalIndex}
                goalVersion={goalVersion}
              />
            )}
          {/* Percentage - PCG - V2 mastery criteria - same for v1 and v2 goals */}
          {goal?.data_collection_method === TPGoalDataCollectionMethod.PERCENTAGE &&
            goal?.goal_type === GoalTypes.Caregiver &&
            goal?.resource_versions?.baseline_measurement === BaselineMeasurementVersion.V2 && (
              <SinglePercentageMasteryCriteriaV2
                goal={goal}
                isEditable={isEditable}
                goalIndex={goalIndex}
                goalVersion={goalVersion}
              />
            )}
        </Box>
      )}
      {goalVersion === TreatmentPlanResourceVersion.V2 && (
        <Grid container>
          <Grid item xs={3}>
            <Typography fontWeight={'bold'} align="left" mb={1}>
              Expected mastery date*
            </Typography>
            {goal?.is_editing ? (
              <FHAntdDateTimePicker
                data-testid="expectedMasteryDatePicker"
                format="MM/YY"
                picker={'month'}
                allowClear={false}
                colorTheme={Themes.Primary}
                coloredBackground
                inputHeight={40}
                style={{ width: '100%' }}
                disabled={!isEditable}
                value={goal.expected_mastery_date ? moment(goal.expected_mastery_date) : ''}
                onChange={(value: Moment) =>
                  onUpdateField(
                    'expected_mastery_date',
                    value?.startOf('month').format('YYYY-MM-DD'),
                  )
                }
                error={errors && errors['expected_mastery_date']}
              />
            ) : goal.expected_mastery_date === null ? (
              <ErrorBox />
            ) : (
              <Typography align="left">
                {goal.expected_mastery_date
                  ? moment(goal.expected_mastery_date).format('MM/YY')
                  : 'N/A'}
              </Typography>
            )}
          </Grid>
        </Grid>
      )}
      {goal?.resource_versions?.baseline_measurement === BaselineMeasurementVersion.V1 &&
        goal?.is_editing && <Divider sx={{ color: FORTA_GRAY2, borderWidth: '1px', my: 3 }} />}
      <FHConfirmDialog
        titleRenderer={() => (
          <Box sx={{ display: 'flex' }}>
            <WarningAmberRoundedIcon sx={{ color: FORTA_ORANGE }} />
            <Typography fontWeight={500} ml={1}>
              Are you sure you want to convert the goal to the new version?
            </Typography>
          </Box>
        )}
        dialogHeight="auto"
        open={showConfirmation}
        contentRenderer={() =>
          goal?.goal_type === GoalTypes.Caregiver ? (
            <MigrationModalPCGContent goal={goal} migrationPreviewState={migrationPreviewState} />
          ) : goal?.goal_type === GoalTypes.SkillAcquisition ? (
            goalVersion === TreatmentPlanResourceVersion.V1 ? (
              <MigrationModalSAGV1GoalContent
                goal={goal}
                migrationPreviewState={migrationPreviewState}
              />
            ) : (
              <MigrationModalSAGV2GoalContent
                goal={goal}
                migrationPreviewState={migrationPreviewState}
              />
            )
          ) : null
        }
        submitStartIcon={<CheckCircleOutline />}
        cancelStartIcon={<HighlightOff />}
        submittext="Confirm"
        canceltext="Cancel"
        hideCancelButton={false}
        onClose={(_, reason) => {
          if (reason && reason === 'backdropClick') return;
          handleCloseMigrationModal();
        }}
        onSubmit={() => {
          handleBaselineVersionUpdate(false);
        }}
      />
    </Box>
  );
}

export default BaselineAndMasteryCriteria;
